import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  InputPropsStrict,
  TablePageChangeEvent,
  TableProps,
} from '@servicetitan/design-system';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { fetchTechnicians } from '@api/queries/getTechnicians';
import { MAX_RETRY_ATTEMPTS } from '@api/client';
import { readTechnicianCache, writeTechnicianCache } from './technicianCache';
import type { TechnicianTableRow } from './types';

export type { TechnicianTableRow } from './types';

export const useTechnicianTableState = () => {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  const tenantMap = useMemo(
    () => Object.fromEntries((user?.org.tenants ?? []).map((t) => [t.id, t])),
    [user?.org.tenants],
  );

  const sortedTenantIds = useMemo(
    () =>
      [...(user?.org.tenants ?? [])]
        .map((t) => t.id)
        .sort((a, b) => a.localeCompare(b)),
    [user?.org.tenants],
  );

  const tenantIdsKey = sortedTenantIds.join(',');

  const [technicians, setTechnicians] = useState<TechnicianTableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [allFailed, setAllFailed] = useState(false);
  const [hasPartialError, setHasPartialError] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState({ skip: 0, take: 4 });
  const [selectedId, setSelectedId] = useState('');

  const fetchedForKeyRef = useRef<string | null>(null);

  const buildRows = useCallback(
    (
      tenantData: {
        tenantId: string;
        technicians: Array<{ id: string; name: string; phone?: string | null }>;
      }[],
    ): TechnicianTableRow[] => {
      return tenantData.flatMap((td) =>
        td.technicians.map((tech) => ({
          id: tech.id,
          name: tech.name,
          phone: tech.phone ?? '',
          tenantId: td.tenantId,
          tenantName:
            tenantMap[td.tenantId]?.companyName ??
            tenantMap[td.tenantId]?.name ??
            td.tenantId,
        })),
      );
    },
    [tenantMap],
  );

  /**
   * Loads technicians for the given tenant IDs with progressive retry on partial failures.
   *
   * - backgroundRefresh: when true, keeps displaying existing data while fetching;
   *   the UI is only updated atomically after the full fetch completes.
   * - When false (foreground): streams progressive updates to the table as each
   *   batch succeeds so the user sees data as soon as possible.
   */
  const loadTechnicians = useCallback(
    async (tenantIds: string[], backgroundRefresh = false) => {
      const accumulated: TechnicianTableRow[] = [];

      if (!backgroundRefresh) {
        setHasPartialError(false);
        setAllFailed(false);
      }

      const fetchAndAccumulate = async (
        ids: string[],
        retry = 0,
      ): Promise<boolean> => {
        const result = await fetchTechnicians(ids);
        const newRows = buildRows(result.data);

        accumulated.push(...newRows);

        // Stream progressive updates to UI only during foreground loads
        if (!backgroundRefresh && newRows.length > 0) {
          setTechnicians([...accumulated]);
        }

        if (result.errors.length > 0 && retry < MAX_RETRY_ATTEMPTS) {
          const failedIds = result.errors.map((e) => e.tenantId);
          return fetchAndAccumulate(failedIds, retry + 1);
        }

        return result.errors.length === 0;
      };

      try {
        const allSucceeded = await fetchAndAccumulate(tenantIds);

        // Only show partial error banner when we have some data but not everything loaded
        if (!allSucceeded && !backgroundRefresh && accumulated.length > 0) {
          setHasPartialError(true);
        } else if (allSucceeded && backgroundRefresh) {
          setHasPartialError(false);
        }

        if (accumulated.length > 0) {
          writeTechnicianCache(userId, accumulated, tenantIds);
          // For background refresh, do an atomic UI swap once all data is ready
          if (backgroundRefresh) {
            setTechnicians([...accumulated]);
          }
        } else if (!backgroundRefresh && !allSucceeded) {
          // No data AND fetch errors → show full error state
          setAllFailed(true);
        }
        // accumulated.length === 0 && allSucceeded → "No technicians found" empty state
      } catch {
        if (!backgroundRefresh) {
          setAllFailed(true);
        }
      } finally {
        if (!backgroundRefresh) {
          setLoading(false);
        }
      }
    },
    [buildRows, userId],
  );

  useEffect(() => {
    if (!userId || fetchedForKeyRef.current === tenantIdsKey) {
      return;
    }

    if (sortedTenantIds.length === 0) {
      setLoading(false);
      return;
    }

    fetchedForKeyRef.current = tenantIdsKey;

    const cached = readTechnicianCache(userId);
    const cachedKey =
      cached?.tenantIds
        .slice()
        .sort((a, b) => a.localeCompare(b))
        .join(',') ?? '';
    const isMatchingCache = cached && cachedKey === tenantIdsKey;

    if (isMatchingCache) {
      setTechnicians(cached.data);
      setLoading(false);

      if (cached.expiresAt < Date.now()) {
        // Cache expired — refresh quietly in background, keeping cached data visible
        loadTechnicians(sortedTenantIds, true);
      }
    } else {
      loadTechnicians(sortedTenantIds, false);
    }
    // tenantIdsKey is a stable string derived from sortedTenantIds; both are
    // included to satisfy the lint rule while avoiding an array in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tenantIdsKey]);

  const searchString = useMemo(() => search.trim().toLowerCase(), [search]);

  useEffect(() => {
    setPage((prev) => ({ skip: 0, take: prev.take }));
  }, [searchString]);

  const dataFiltered = useMemo(() => {
    if (!searchString) return technicians;
    return technicians.filter((t) =>
      [t.name, t.phone, t.tenantName].some((val) =>
        val?.toLowerCase().includes(searchString),
      ),
    );
  }, [searchString, technicians]);

  const dataPaginated = useMemo(
    () => dataFiltered.slice(page.skip, page.skip + page.take),
    [dataFiltered, page],
  );

  const onSearchChange: InputPropsStrict['onChange'] = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [],
  );

  const onPageChange: TableProps['onPageChange'] = useCallback(
    (event: TablePageChangeEvent) => {
      setPage(event.page);
    },
    [],
  );

  const onSelectionChange = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  return {
    data: dataPaginated,
    total: dataFiltered.length,
    loading,
    allFailed,
    hasPartialError,
    search,
    page,
    selectedId,
    onSearchChange,
    onPageChange,
    onSelectionChange,
  };
};
